import { useState, useEffect, useCallback } from 'react';
import { Settings, Image, FileText, MapPin, Search, LogOut, Loader2, Plus, Trash2, Eye, EyeOff, Upload } from 'lucide-react';
import api from '@/utils/api';

const TABS = [
  { id: 'settings', label: 'Settings', Icon: Settings },
  { id: 'banners', label: 'Banners', Icon: Image },
  { id: 'blog', label: 'Blog', Icon: FileText },
  { id: 'cities', label: 'Cities & Scale', Icon: MapPin },
  { id: 'indexnow', label: 'IndexNow', Icon: Search }
];

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState('settings');
  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <header className="bg-cwc-navy text-white px-6 py-4 flex items-center justify-between">
        <h1 className="font-bold text-lg">Admin Dashboard</h1>
        <button onClick={onLogout} className="text-white/60 hover:text-white flex items-center gap-2 text-sm" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
      </header>
      <div className="flex border-b bg-white">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-cwc-gold text-cwc-navy' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`admin-tab-${t.id}`}>
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      <div className="max-w-5xl mx-auto p-6">
        {tab === 'settings' && <SettingsTab headers={headers} />}
        {tab === 'banners' && <BannersTab headers={headers} />}
        {tab === 'blog' && <BlogTab headers={headers} />}
        {tab === 'cities' && <CitiesTab headers={headers} />}
        {tab === 'indexnow' && <IndexNowTab headers={headers} />}
      </div>
    </div>
  );
}

function SettingsTab({ headers }) {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/admin/settings', { headers }).then(r => setSettings(r.data)).catch(() => {});
  }, [headers]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings, { headers });
      setMsg('Saved!');
      setTimeout(() => setMsg(''), 2000);
    } catch { setMsg('Error saving'); }
    setSaving(false);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-settings">
      <h2 className="text-lg font-bold text-cwc-navy mb-4">Site Settings</h2>
      <div className="space-y-4 max-w-lg">
        <div><label className="text-sm font-medium text-gray-700 block mb-1">Site Name</label><input value={settings.site_name || ''} onChange={e => setSettings({...settings, site_name: e.target.value})} className="w-full px-3 py-2 rounded-lg border text-sm" /></div>
        <div><label className="text-sm font-medium text-gray-700 block mb-1">Contact Email</label><input value={settings.contact_email || ''} onChange={e => setSettings({...settings, contact_email: e.target.value})} className="w-full px-3 py-2 rounded-lg border text-sm" /></div>
        <div><label className="text-sm font-medium text-gray-700 block mb-1">GHL Webhook URL</label><input value={settings.ghl_webhook_url || ''} onChange={e => setSettings({...settings, ghl_webhook_url: e.target.value})} className="w-full px-3 py-2 rounded-lg border text-sm" /></div>
        <label className="flex items-center gap-2"><input type="checkbox" checked={settings.adsense_enabled || false} onChange={e => setSettings({...settings, adsense_enabled: e.target.checked})} /> <span className="text-sm">AdSense Enabled</span></label>
        <button onClick={save} disabled={saving} className="bg-cwc-navy text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-cwc-navy/90" data-testid="admin-settings-save">{saving ? 'Saving...' : 'Save Settings'}</button>
        {msg && <p className="text-green-600 text-sm">{msg}</p>}
      </div>
    </div>
  );
}

function BannersTab({ headers }) {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({ title: '', image_url: '', link_url: '', position: 'sidebar', active: true });

  const load = useCallback(() => { api.get('/admin/banners', { headers }).then(r => setBanners(r.data.banners || [])).catch(() => {}); }, [headers]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title) return;
    await api.post('/admin/banners', form, { headers });
    setForm({ title: '', image_url: '', link_url: '', position: 'sidebar', active: true });
    load();
  };

  const remove = async (id) => { await api.delete(`/admin/banners/${id}`, { headers }); load(); };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-banners">
      <h2 className="text-lg font-bold text-cwc-navy mb-4">Banners</h2>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input placeholder="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]" />
        <input placeholder="Image URL" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} className="px-3 py-2 rounded-lg border text-sm flex-1" />
        <input placeholder="Link URL" value={form.link_url} onChange={e => setForm({...form, link_url: e.target.value})} className="px-3 py-2 rounded-lg border text-sm flex-1" />
        <button onClick={add} className="bg-cwc-gold text-cwc-navy px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1" data-testid="admin-add-banner"><Plus className="w-4 h-4" /> Add</button>
      </div>
      <div className="space-y-2">
        {banners.map(b => (
          <div key={b.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
            <div><p className="font-medium text-sm">{b.title}</p><p className="text-xs text-gray-400">{b.link_url}</p></div>
            <button onClick={() => remove(b.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {banners.length === 0 && <p className="text-gray-400 text-sm">No banners yet</p>}
      </div>
    </div>
  );
}

function BlogTab({ headers }) {
  const [posts, setPosts] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [generating, setGenerating] = useState(false);
  const [msg, setMsg] = useState('');
  const [autoPublish, setAutoPublish] = useState(false);
  const [autoStats, setAutoStats] = useState({});

  const load = useCallback(() => {
    api.get('/blog?limit=50&published_only=false', { headers }).then(r => setPosts(r.data.posts || [])).catch(() => {});
    api.get('/admin/blog/topics', { headers }).then(r => setTopics(r.data.topics || [])).catch(() => {});
    api.get('/admin/blog/auto-publish', { headers }).then(r => {
      setAutoPublish(r.data.enabled || false);
      setAutoStats(r.data);
    }).catch(() => {});
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!selectedTopic) return;
    setGenerating(true); setMsg('');
    try {
      await api.post('/admin/blog/generate', { topic: selectedTopic }, { headers });
      setMsg('Post generated!');
      load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  const toggleAutoPublish = async () => {
    try {
      const { data } = await api.put('/admin/blog/auto-publish', { enabled: !autoPublish }, { headers });
      setAutoPublish(data.enabled);
      setMsg(data.message);
    } catch { setMsg('Failed to update auto-publish'); }
  };

  const togglePublish = async (id) => {
    await api.patch(`/admin/blog/${id}/publish`, {}, { headers });
    load();
  };

  const deletePost = async (id) => {
    await api.delete(`/admin/blog/${id}`, { headers });
    load();
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-blog">
      <h2 className="text-lg font-bold text-cwc-navy mb-4">Blog Management</h2>

      {/* Auto-publish toggle */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6 flex items-center justify-between" data-testid="admin-auto-publish">
        <div>
          <p className="font-medium text-cwc-navy text-sm">Weekly Auto-Publish</p>
          <p className="text-xs text-gray-400">{autoStats.posts_generated || 0} of {autoStats.max_posts || 52} posts generated | {topics.filter(t => !t.used).length} topics remaining</p>
        </div>
        <button onClick={toggleAutoPublish} className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${autoPublish ? 'bg-green-500' : 'bg-gray-300'}`} data-testid="admin-auto-publish-toggle">
          <span className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform ${autoPublish ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* Generate new post */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <select value={selectedTopic} onChange={e => setSelectedTopic(e.target.value)} className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[250px]" data-testid="admin-blog-topic-select">
          <option value="">Select topic to generate...</option>
          {topics.filter(t => !t.used).map(t => <option key={t.topic} value={t.topic}>{t.topic}</option>)}
        </select>
        <button onClick={generate} disabled={generating || !selectedTopic} className="bg-cwc-gold text-cwc-navy px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1" data-testid="admin-blog-generate">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Plus className="w-4 h-4" /> Generate</>}
        </button>
      </div>
      {msg && <p className="text-sm mb-4 text-green-600">{msg}</p>}
      <div className="space-y-2">
        {posts.map(p => (
          <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-testid={`admin-blog-post-${p.id}`}>
            <div className="flex-1 min-w-0"><p className="font-medium text-sm truncate">{p.title}</p><p className="text-xs text-gray-400">{p.published ? 'Published' : 'Draft'}</p></div>
            <div className="flex items-center gap-2 ml-3">
              <button onClick={() => togglePublish(p.id)} className={`p-1.5 rounded ${p.published ? 'text-green-600' : 'text-gray-400'}`} title={p.published ? 'Unpublish' : 'Publish'}>{p.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
              <button onClick={() => deletePost(p.id)} className="p-1.5 rounded text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <p className="text-gray-400 text-sm">No blog posts yet. Generate one above!</p>}
      </div>
    </div>
  );
}

function CitiesTab({ headers }) {
  const [cities, setCities] = useState([]);
  const [total, setTotal] = useState(0);
  const [newCity, setNewCity] = useState('');
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    api.get('/cities?limit=500', { headers }).then(r => { setCities(r.data.cities || []); setTotal(r.data.total || 0); }).catch(() => {});
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const addCity = async () => {
    if (!newCity) return;
    try { await api.post('/admin/cities', { name: newCity }, { headers }); setNewCity(''); load(); setMsg('City added!'); } catch (e) { setMsg(e.response?.data?.detail || 'Error'); }
  };

  const uploadCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/admin/cities/upload', formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setMsg(data.message);
      load();
    } catch { setMsg('Upload failed'); }
    setUploading(false);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-cities">
      <h2 className="text-lg font-bold text-cwc-navy mb-4">Cities & Scale ({total} cities)</h2>
      <div className="flex gap-2 mb-4 flex-wrap">
        <input placeholder="Add city name" value={newCity} onChange={e => setNewCity(e.target.value)} className="px-3 py-2 rounded-lg border text-sm flex-1" data-testid="admin-add-city-input" />
        <button onClick={addCity} className="bg-cwc-gold text-cwc-navy px-4 py-2 rounded-lg text-sm font-medium" data-testid="admin-add-city-btn"><Plus className="w-4 h-4 inline mr-1" />Add</button>
        <label className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer flex items-center gap-1 hover:bg-gray-200">
          <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload CSV'}
          <input type="file" accept=".csv" onChange={uploadCSV} className="hidden" data-testid="admin-csv-upload" />
        </label>
      </div>
      {msg && <p className="text-sm mb-4 text-green-600">{msg}</p>}
      <div className="max-h-96 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-1">
        {cities.map(c => (
          <div key={c.slug} className="text-sm text-gray-600 px-2 py-1">{c.name}</div>
        ))}
      </div>
    </div>
  );
}

function IndexNowTab({ headers }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submitAll = async () => {
    setSubmitting(true);
    try {
      const citiesRes = await api.get('/cities?limit=500');
      const cities = citiesRes.data.cities || [];
      const urls = ['https://compareworkerscomp.com/', 'https://compareworkerscomp.com/workers-comp-insurance-california', 'https://compareworkerscomp.com/blog'];
      const slugs = ['construction', 'restaurants', 'home-health', 'landscaping', 'trucking', 'all-businesses'];
      slugs.forEach(s => urls.push(`https://compareworkerscomp.com/workers-comp-for-${s}-companies`));
      cities.forEach(c => {
        urls.push(`https://compareworkerscomp.com/workers-comp-insurance-${c.slug}-ca`);
        slugs.forEach(s => urls.push(`https://compareworkerscomp.com/workers-comp-for-${s}-in-${c.slug}-ca`));
      });
      // Submit in batches of 500
      const batches = [];
      for (let i = 0; i < urls.length; i += 500) batches.push(urls.slice(i, i + 500));
      const results = [];
      for (const batch of batches) {
        const { data } = await api.post('/admin/indexnow', { urls: batch }, { headers });
        results.push(data);
      }
      setResult({ total: urls.length, batches: results.length });
    } catch (e) { setResult({ error: 'Submission failed' }); }
    setSubmitting(false);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm" data-testid="admin-indexnow">
      <h2 className="text-lg font-bold text-cwc-navy mb-4">IndexNow Submission</h2>
      <p className="text-gray-500 text-sm mb-4">Submit all site URLs to Bing for indexing. Uses bing.com/indexnow endpoint. Max 500 per batch.</p>
      <button onClick={submitAll} disabled={submitting} className="bg-cwc-gold text-cwc-navy px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2" data-testid="admin-indexnow-submit">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Search className="w-4 h-4" /> Submit All URLs</>}
      </button>
      {result && (
        <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm">
          {result.error ? <p className="text-red-600">{result.error}</p> : <p className="text-green-600">Submitted {result.total} URLs in {result.batches} batch(es)</p>}
        </div>
      )}
    </div>
  );
}
